import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getReservationById, Reservation } from '../api/reservationApi';
import Logo from '../componets/Logo';
import Footer from '../componets/Footer';
import { authFetch } from '../utils/AuthFetch';
import { getPaymentByOrderId } from '../api/paymentApi';

interface Payment {
  id: number;
  amount: number;
  transaction_id: string;
  order_id: string;
  pay_type: string;
  created_at: string;
  updated_at: string;
}

const PaymentDetailPage: React.FC = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
  const { reservationId } = useParams<{ reservationId: string }>();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [checklist, setChecklist] = useState({
    item1: false,
    item2: false,
    item3: false,
  });

  // Fetch reservation and payment data
  useEffect(() => {
    const loadReservationAndPayment = async () => {
      try {
        if (!reservationId) {
          throw new Error('Reservation ID is missing.');
        }

        const reservationData = await getReservationById(Number(reservationId));
        setReservation(reservationData);
  
        if (reservationData && reservationData.order_id) {
          const paymentData = await getPaymentByOrderId(reservationData.order_id);
          setPayment(paymentData);
        } else {
          throw new Error('Order ID is missing.');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
  
    loadReservationAndPayment();
  }, [reservationId]);

  const confirmCancelPayment = () => {
    if (!reservation) return;
    setIsModalOpen(true);
  };

  const handleCancelPayment = async () => {
    if (!reservation) return;

    const cancelPaymentData = {
      orderNumber: reservation.order_id,
      reason: "User requested cancellation",
    };

    try {
      const response = await authFetch(`${API_BASE_URL}/payment/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cancelPaymentData),
      });

      const data = await response.json();

      if (response.ok) {
        alert(`결제 취소 요청이 전송되었습니다. 예약 ID: ${reservation.id}`);
        setReservation((prevReservation) => ({
          ...prevReservation!,
          status: 'CANCELED',  
        }));
        setIsModalOpen(false);
      } else {
        alert(`결제 취소 실패: ${data.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error canceling payment:', error);
      alert('결제 취소 중 오류가 발생했습니다.');
      setIsModalOpen(false); 
    }
  };

  const handleChecklistChange = (key: keyof typeof checklist) => {
    setChecklist((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) return <div className="min-h-screen bg-gray-50 p-6">Loading...</div>;
  if (!reservation || !payment) return <div>No reservation or payment found.</div>;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="px-4 lg:px-6 py-4">
        <Logo />
        <h2 className="text-2xl font-semibold mb-4 mt-6">결제 정보</h2>
      </header>

      <div className="flex-1 px-4 lg:px-6 py-4">
        <div className="border rounded shadow p-4">
          <h3 className="text-lg font-semibold">{reservation.space.title}</h3>
          
          {/* Payment Details */}
          <div className="mt-4">
            <h4 className="font-semibold">결제 정보</h4>
            <p>주문 번호: {payment.order_id}</p>
            <p>결제 금액: {payment.amount}원</p>
            <p>결제일: {new Date(payment.created_at).toLocaleString()}</p>
            <p>결제 수단: {payment.pay_type}</p>
          </div>

          {/* 결제 취소 버튼 */}
          {reservation.status === 'PAYMENT_COMPLETED' && (
            <button
              onClick={confirmCancelPayment}
              className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 mt-4"
            >
              결제 취소
            </button>
          )}
        </div>
      </div>

      {/* 결제 취소 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-xl font-semibold mb-4">결제 취소 확인</h3>
            <div className="mb-4">
              <label className="block font-medium mb-2">이 결제 취소를 진행하시겠습니까?</label>
              <textarea
                readOnly
                className="w-full p-2 border rounded bg-gray-100 text-sm mb-4"
                rows={4}
                value={
                  '이 결제 취소는 복구할 수 없습니다. 결제를 취소하려면 아래 항목을 확인해 주세요.'
                }
              />
            </div>

            <div className="mb-4">
              {['안내사항에 동의합니다.', '결제 취소 후에는 복구할 수 없습니다.'].map((text, i) => {
                const key = `item${i + 1}` as keyof typeof checklist;
                return (
                  <label key={key} className="flex items-center mb-2">
                    <input
                      type="checkbox"
                      checked={checklist[key]}
                      onChange={() => handleChecklistChange(key)}
                      className="mr-2"
                    />
                    {text}
                  </label>
                );
              })}
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                취소
              </button>
              <button
                onClick={handleCancelPayment}
                disabled={!checklist.item1 || !checklist.item2}
                className={`px-4 py-2 rounded ${!checklist.item1 || !checklist.item2
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PaymentDetailPage;
